import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import ToggleSwitch from 'components/ToggleSwitch';
import { SegmentedToggle } from 'components/SegmentedToggle';
import { AssetUrls } from 'config/assets';
import { useGetNotificationSettingsQuery, useUpdateNotificationSettingsMutation } from 'store/services/message/messageApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { NotificationSettings, SuggestionEmailFrequency } from 'types/interfaces/message';
import { SettingsCard, SettingsRow, SettingsSection } from '../SettingsRow/SettingsRow';
import { NOTIFICATION_GROUPS, NotificationRow, NotificationRowKey } from './notificationRows';
import './NotificationsTab.css';

const SAVE_FAILED = 'Failed to update notification preferences. Please try again.';

const EyeIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

/** The example email for a row: label, subject, the preview image, and what turning it off costs. */
const EmailPreview: React.FC<{ row: NotificationRow }> = ({ row }) => (
  <div className="email-preview" role="tooltip" data-testid={`email-preview-${row.key}`}>
    <div className="email-preview__label">Example email</div>
    <div className="email-preview__subject">{row.subject}</div>
    <img
      className="email-preview__image"
      src={AssetUrls.emailPreviews[row.key]}
      alt={`Example of the "${row.label}" email`}
      width={420}
      height={466}
      loading="lazy"
    />
    <p className="email-preview__note">{row.offNote}</p>
  </div>
);

/**
 * Asked before turning OFF one of the three response-critical emails. Escape,
 * the overlay and "Keep on" all leave the toggle on and make no request.
 */
const WarningModal: React.FC<{ row: NotificationRow; onKeep: () => void; onTurnOff: () => void }> = ({
  row,
  onKeep,
  onTurnOff,
}) => {
  const keepRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    keepRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onKeep();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onKeep]);

  return createPortal(
    <div className="notif-warning-overlay" onClick={onKeep} data-testid="notif-warning">
      <div
        className="notif-warning"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="notif-warning-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="notif-warning__icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 3.5L22 20.5H2L12 3.5z" stroke="#f59e0b" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M12 10v5" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="12" cy="17.6" r="1" fill="#f59e0b" />
          </svg>
        </div>
        <h2 id="notif-warning-title" className="notif-warning__title">
          Turn off {row.warning?.what}?
        </h2>
        <p className="notif-warning__body">
          Traders on TradeMagic are expected to respond to their trading partners within{' '}
          <strong className="notif-warning__hours">72 hours</strong> on active trades.
        </p>
        <p className="notif-warning__body">{row.warning?.consequence}</p>
        <div className="notif-warning__actions">
          <button type="button" className="notif-warning__btn notif-warning__btn--secondary" onClick={onTurnOff}>
            Turn off anyway
          </button>
          <button type="button" className="notif-warning__btn notif-warning__btn--primary" onClick={onKeep} ref={keepRef}>
            Keep on
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/**
 * Settings > Notifications: 13 email toggles in 4 groups, each with a preview
 * of the real email (hover the row, or pin it with Preview), then trade
 * suggestions and sounds. Toggles save on change.
 */
const NotificationsTab: React.FC = () => {
  const { data: settings, isLoading } = useGetNotificationSettingsQuery();
  const [updateSettings] = useUpdateNotificationSettingsMutation();
  const { data: me } = useGetMeQuery();
  const [pinned, setPinned] = useState<NotificationRowKey | null>(null);
  const [hovered, setHovered] = useState<NotificationRowKey | null>(null);
  const [confirming, setConfirming] = useState<NotificationRow | null>(null);

  const save = async (patch: Partial<NotificationSettings>) => {
    try {
      await updateSettings(patch).unwrap();
    } catch (error) {
      console.error('Failed to update notification settings:', error);
      toast.error(SAVE_FAILED);
    }
  };

  const handleToggle = (row: NotificationRow, checked: boolean) => {
    // Turning one of the response-critical emails OFF asks first; turning it back on never does.
    if (!checked && row.warning) {
      setConfirming(row);
      return;
    }
    save({ [row.field]: checked });
  };

  const isPartner = !!me?.isAffiliate;
  const shown = pinned ?? hovered;

  return (
    <div className="notifications-tab">
      <div className="notifications-tab__head">
        <h2 className="notifications-tab__title">Email notifications</h2>
        <span className="notifications-tab__hint">Hover a row to see the email</span>
      </div>

      {NOTIFICATION_GROUPS.map((group) => {
        const rows = group.rows.filter((row) => !row.partnersOnly || isPartner);
        if (rows.length === 0) return null;
        return (
          <SettingsSection key={group.label} label={group.label}>
            <SettingsCard>
              {rows.map((row) => (
                <div
                  key={row.key}
                  className={`notif-row ${shown === row.key ? 'notif-row--open' : ''}`}
                  onMouseEnter={() => setHovered(row.key)}
                  onMouseLeave={() => setHovered((current) => (current === row.key ? null : current))}
                  data-testid={`notif-row-${row.key}`}
                >
                  <SettingsRow
                    title={row.label}
                    description={row.sublabel}
                    below={
                      <button
                        type="button"
                        className={`notif-preview-link ${pinned === row.key ? 'notif-preview-link--pinned' : ''}`}
                        aria-pressed={pinned === row.key}
                        onClick={() => setPinned((current) => (current === row.key ? null : row.key))}
                      >
                        <EyeIcon />
                        Preview
                      </button>
                    }
                    control={
                      <ToggleSwitch
                        // A value the API does not send (an older backend, or no settings row) is ON.
                        checked={settings?.[row.field] ?? true}
                        onChange={(checked) => handleToggle(row, checked)}
                        disabled={isLoading}
                        ariaLabel={row.label}
                      />
                    }
                  />
                  {shown === row.key && <EmailPreview row={row} />}
                </div>
              ))}
            </SettingsCard>
          </SettingsSection>
        );
      })}

      <div className="notifications-tab__always">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
          <path d="M5.5 7V5a2.5 2.5 0 015 0v2" stroke="currentColor" strokeWidth="1.3" />
        </svg>
        <span>
          Security and account emails are always sent: password resets, email address changes, and verification
          links.
        </span>
      </div>

      <SettingsSection label="Trade suggestions">
        <SettingsCard>
          <SettingsRow
            stacked
            title="Trade suggestion emails"
            description="Trades we find between your Have List and other traders' want lists."
            control={
              <SegmentedToggle<SuggestionEmailFrequency>
                className="segmented-toggle--calm"
                ariaLabel="Trade suggestion emails"
                value={settings?.suggestionEmailFrequency ?? 'off'}
                onChange={(value) => save({ suggestionEmailFrequency: value })}
                disabled={isLoading}
                options={[
                  { value: 'off', label: 'Off' },
                  { value: 'daily', label: 'Daily digest (recommended)' },
                  { value: 'realtime', label: 'Real time' },
                ]}
              />
            }
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection label="Sounds">
        <SettingsCard>
          <SettingsRow
            title="Point balance sounds"
            description="Play coin sounds when your point balance changes."
            control={
              <ToggleSwitch
                checked={settings?.soundEnabled ?? true}
                onChange={(checked) => save({ soundEnabled: checked })}
                disabled={isLoading}
                ariaLabel="Point balance sounds"
              />
            }
          />
        </SettingsCard>
      </SettingsSection>

      <p className="settings-note">SMS alerts for your Have List are set in Preferences.</p>

      {confirming && (
        <WarningModal
          row={confirming}
          onKeep={() => setConfirming(null)}
          onTurnOff={() => {
            const row = confirming;
            setConfirming(null);
            save({ [row.field]: false });
          }}
        />
      )}
    </div>
  );
};

export default NotificationsTab;
